import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle, Send } from 'lucide-react';

export function playSentHandoffChime(): void {
  if (typeof window === 'undefined') return;

  try {
    const AudioContextCtor = window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    const audioContext = new AudioContextCtor();
    const now = audioContext.currentTime;
    const gain = audioContext.createGain();
    gain.connect(audioContext.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

    const playTone = (frequency: number, offset: number, duration: number) => {
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, now + offset);
      oscillator.connect(gain);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + duration);
    };

    playTone(659.25, 0, 0.11);
    playTone(880, 0.12, 0.16);
    window.setTimeout(() => {
      void audioContext.close().catch(() => undefined);
    }, 650);
  } catch {
    // Browsers may block audio; the visual handoff still completes.
  }
}

interface SentHandoffOverlayProps {
  show: boolean;
  title: string;
  description: string;
}

export function SentHandoffOverlay({ show, title, description }: SentHandoffOverlayProps) {
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/35 backdrop-blur-sm pointer-events-none print:hidden"
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative overflow-hidden rounded-2xl border border-emerald-300 bg-card px-8 py-7 shadow-2xl"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.92, y: 18 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: -10 }}
            transition={{ duration: reduceMotion ? 0.12 : 0.32, ease: 'easeOut' }}
          >
            <div className="flex items-center gap-5">
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                {!reduceMotion && (
                  <motion.span
                    className="absolute inset-0 rounded-full border-2 border-emerald-300"
                    initial={{ scale: 0.75, opacity: 0.8 }}
                    animate={{ scale: 1.55, opacity: 0 }}
                    transition={{ duration: 0.75, ease: 'easeOut' }}
                  />
                )}
                <motion.div
                  initial={reduceMotion ? undefined : { x: -10, y: 8, rotate: -10 }}
                  animate={reduceMotion ? undefined : { x: 0, y: 0, rotate: 0 }}
                  transition={{ duration: 0.36, ease: 'easeOut' }}
                >
                  <Send className="h-8 w-8" />
                </motion.div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <p className="text-lg font-bold text-foreground">{title}</p>
                </div>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {description}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
